import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, Package, Users, Truck, Wallet } from 'lucide-react';

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="empty">Loading dashboard...</div>;
  const s = data.stats || {};

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card green">
          <div className="stat-label">Total Sales</div>
          <div className="stat-value">{formatCurrency(s.total_sales)}</div>
          <div className="stat-sub">This month: {formatCurrency(s.month_sales)}</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">Total Purchases</div>
          <div className="stat-value">{formatCurrency(s.total_purchases)}</div>
          <div className="stat-sub">This month: {formatCurrency(s.month_purchases)}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">Operational Expenses</div>
          <div className="stat-value">{formatCurrency(s.total_expenses)}</div>
          <div className="stat-sub">This month: {formatCurrency(s.month_expenses)}</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Net Profit</div>
          <div className="stat-value" style={{ color: s.gross_profit >= 0 ? '#059669' : '#dc2626' }}>
            {formatCurrency(s.gross_profit)}
          </div>
          <div className="stat-sub">This month: {formatCurrency(s.month_profit)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Receivables</div>
          <div className="stat-value">{formatCurrency(s.receivables)}</div>
          <div className="stat-sub">From customer ledgers</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">Payables</div>
          <div className="stat-value">{formatCurrency(s.payables)}</div>
          <div className="stat-sub">To supplier ledgers</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><Users size={12} style={{ verticalAlign: 'middle' }} /> Parties</div>
          <div className="stat-value">{s.total_ledgers}</div>
          <div className="stat-sub">{s.total_ledgers} ledgers ({s.total_suppliers} suppliers, {s.total_customers} customers)</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label"><Truck size={12} style={{ verticalAlign: 'middle' }} /> Fleet</div>
          <div className="stat-value">{s.total_vehicles}</div>
          <div className="stat-sub">{s.total_products} products in stock</div>
        </div>
      </div>

      {data.vehicle_alerts?.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="card-header"><h3><AlertCircle size={16} style={{verticalAlign:'middle'}} /> Vehicle Document Alerts (next 30 days)</h3></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Vehicle</th><th>Insurance</th><th>Fitness</th><th>Permit</th><th>Pollution</th></tr></thead>
              <tbody>
                {data.vehicle_alerts.map(v => (
                  <tr key={v.id}>
                    <td><strong>{v.vehicle_no}</strong></td>
                    <td>{formatDate(v.insurance_expiry)}</td>
                    <td>{formatDate(v.fitness_expiry)}</td>
                    <td>{formatDate(v.permit_expiry)}</td>
                    <td>{formatDate(v.pollution_expiry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="two-col-grid" style={{marginBottom:16}}>
        <div className="card">
          <div className="card-header"><h3>Monthly Trend (Last 6 months)</h3></div>
          <div className="chart-wrap">
            <ResponsiveContainer>
              <LineChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="sales" stroke="#059669" strokeWidth={2} />
                <Line type="monotone" dataKey="purchases" stroke="#f59e0b" strokeWidth={2} />
                <Line type="monotone" dataKey="expenses" stroke="#dc2626" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Cash Flow Comparison</h3></div>
          <div className="chart-wrap">
            <ResponsiveContainer>
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="sales" fill="#059669" />
                <Bar dataKey="purchases" fill="#f59e0b" />
                <Bar dataKey="expenses" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="two-col-grid">
        <div className="card">
          <div className="card-header"><h3>Recent Sales</h3></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Invoice</th><th>Ledger</th><th>Date</th><th className="td-num">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {data.recent_sales?.length ? data.recent_sales.map(r => (
                  <tr key={r.id}>
                    <td>{r.invoice_no}</td>
                    <td>{r.ledger_name}</td>
                    <td>{formatDate(r.sale_date)}</td>
                    <td className="td-num">{formatCurrency(r.total_amount)}</td>
                    <td><span className={`badge badge-${r.payment_status === 'paid' ? 'green' : r.payment_status === 'partial' ? 'orange' : 'red'}`}>{r.payment_status}</span></td>
                  </tr>
                )) : <tr><td colSpan="5" className="empty">No sales yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Recent Purchases</h3></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Invoice</th><th>Ledger</th><th>Date</th><th className="td-num">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {data.recent_purchases?.length ? data.recent_purchases.map(r => (
                  <tr key={r.id}>
                    <td>{r.invoice_no}</td>
                    <td>{r.ledger_name}</td>
                    <td>{formatDate(r.purchase_date)}</td>
                    <td className="td-num">{formatCurrency(r.total_amount)}</td>
                    <td><span className={`badge badge-${r.payment_status === 'paid' ? 'green' : r.payment_status === 'partial' ? 'orange' : 'red'}`}>{r.payment_status}</span></td>
                  </tr>
                )) : <tr><td colSpan="5" className="empty">No purchases yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {data.low_stock?.length > 0 && (
        <div className="card" style={{ marginTop: 16, borderLeft: '4px solid #dc2626' }}>
          <div className="card-header"><h3><Package size={16} style={{verticalAlign:'middle'}}/> Low Stock Alert</h3></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Product</th><th className="td-num">Current Stock</th><th className="td-num">Min Stock</th><th>Unit</th></tr></thead>
              <tbody>
                {data.low_stock.map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td className="td-num text-red">{p.stock_qty}</td>
                    <td className="td-num">{p.min_stock}</td>
                    <td>{p.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
